import * as React from 'react';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import glamorous from 'glamorous';
import { RvLoader } from '../../components/Loader';
import { GetGroupByPlurals, GetGroupBy, formatNumber, getSuggestedMaxTick, getMaxTickLimit, getLabels } from '../../helpers/ChartHelper';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { ChartCommonProps, IconButtonWrapper, ChartCommonState, HelpWrapper } from '../Common';
import { WithStyles, withStyles, IconButton } from '@material-ui/core';
import { styles } from '../styles';
import { ChartFilter } from '../ChartComponents';
import { SelectedItemProps } from '../MattersByStatusChart';
import { DrillDownType } from '../DrillDownType';

import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, rvBarDatasetStyle, rvChartAnimation, rvValueAxisStyle, rvCategoryAxisStyle, rvTooltipStyle, isChartDataEmpty } from '../chartTheme';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartNotes } from '../ChartNotes';
import { ChartLimitNote } from '../ChartLimitNote';
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const BarWrapper = glamorous.div<{ theme?: object }>((props) => ({
    position: 'relative',
    paddingBottom: '10px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    maxWidth: '100%',
    maxHeight: '100%',
    height: '100%',
    '@media print': {
        display: 'flex',
        flexFlow: 'column',
        alignItems: 'center',
    },
}));

const BarTitleContainer = glamorous.div<{ theme?: object }>((props) => ({
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    padding: '15px 15px 0px 15px',
    fontSize: '16px',
    fontWeight: 600,
    [`& .${`h1`}`]: {},
    [`& .${`h2`}`]: {
        fontSize: '2.5rem',
        padding: '10px 0',
    },
    [`& .${`h3`}`]: {
        paddingTop: '10px',
    },
})).withProps({ className: 'rv-chart-title-area' });

const BarContainer = glamorous.div<{ theme?: object }>((props) => ({
    padding: '5px 15px 15px 15px',
    minHeight: '300px',
    flex: 1,
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: '350px',
    maxWidth: 'auto',
    position: 'relative',
    '@media (max-width: 533px)': {
        maxWidth: '350px',
        margin: '0 auto',
    },
    [`& .${`titleWrapper`}`]: {
        textAlign: 'center',
        display: 'inline-block',
        width: '100%',
        padding: '15px',
        [`& .${`title`}`]: {
            fontSize: '18px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`value`}`]: {
            fontSize: '24px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`secondary-title`}`]: {
            fontSize: '16px',
            fontWeight: 400,
            display: 'block',
            marginBottom: '15px',
        },
    },
}));

const LoadWrapper = glamorous.div<{ theme?: object }>((props) => ({
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
}));

const TotalDiv = glamorous.div<{ theme?: object }>((props) => ({
    cursor: 'pointer',
    paddingRight: 5,
}));

interface ChartProps extends SelectedItemProps, ChartCommonProps, WithStyles<typeof styles> {
    groupBy: string;
    chartFilter?: ChartFilter;
}

interface ChartState extends ChartCommonState {
    //
}

class UnbilledDisbursementsChart extends React.Component<ChartProps, ChartState> {
    // tslint:disable-next-line: no-any
    public chartRef: React.RefObject<any>;

    constructor(props: ChartProps, state: ChartState) {
        super(props, state);
        this.onCloseChart = this.onCloseChart.bind(this);
        this.handlePopoverOpen = this.handlePopoverOpen.bind(this);
        this.handlePopoverClose = this.handlePopoverClose.bind(this);
        this.handleTotalClick = this.handleTotalClick.bind(this);
        this.onHover = this.onHover.bind(this);

        this.onPan = this.onPan.bind(this);

        this.chartRef = React.createRef();

        this.state = {
            open: false,
            anchorEl: undefined,
        };
    }

    public componentDidMount() {
        //
    }

    public componentWillUnmount() {
        //
    }

    public shouldComponentUpdate(nextProps: ChartProps, nextState: ChartState): boolean {
        if (this.props !== nextProps || this.state !== nextState) {
            return true;
        }

        return false;
    }

    public render() {
        // const { classes } = this.props;

        return (
            <React.Fragment>
                {/* tslint:disable-next-line: no-any */}
                <Query<any>
                    query={UnBilledDisbursementsData}
                    variables={{
                        groupBy: this.props.groupBy,
                        entityType: this.props.chartFilter?.entityType,
                        entityGuid: this.props.chartFilter?.entityGuid,
                        responsibility: this.props.chartFilter?.responsibility,
                    }}
                >
                    {({ loading, error, data }) => {
                        if (loading) {
                            return (
                                <ChartSkeleton variant="bars" />
                            );
                        }

                        const statistics = data.statistics;

                        let matter = loading || error || statistics === null || statistics === undefined ? null : statistics.matter;

                        let unBilledDisbursements = matter ? matter.unBilledDisbursements : null;

                        if (unBilledDisbursements && unBilledDisbursements.unBilledDisbursement) {
                            let totalAmount = unBilledDisbursements.total;
                            let labelCount = unBilledDisbursements.groupedByCount;
                            // tslint:disable-next-line:no-any
                            let labels = unBilledDisbursements.unBilledDisbursement.map((value: any) => {
                                return value.groupedBy;
                            });

                            // tslint:disable-next-line: no-any
                            let amounts = unBilledDisbursements.unBilledDisbursement.map((value: any) => {
                                return value.amount;
                            });

                            return (
                                <BarWrapper className={RV_CHART_CLASS}>
                                    <IconButtonWrapper aria-label="close">
                                        <HelpWrapper className="rv-chart-help">
                                            <ChartInfo 
                                                description={this.props.description}
                                            />
                                        </HelpWrapper>
                                        <IconButton aria-label="close" onClick={this.onCloseChart} size="small">
                                            <CloseIcon />
                                        </IconButton>
                                    </IconButtonWrapper>
                                    {/* tslint:disable-next-line: no-any */}
                                    <Query<any> query={CurrencySymbol}>
                                        {({
                                            // tslint:disable-next-line: no-shadowed-variable
                                            loading, error, data,
                                        }) => {
                                            if (loading) {
                                                return (
                                                    <LoadWrapper className="load-wrapper">
                                                        <RvLoader size="small" />
                                                    </LoadWrapper>
                                                );
                                            }

                                            const currencySymbol = loading || error ? null : data.options.currencySymbol;

                                            const symbol = currencySymbol ? currencySymbol.symbol : null;

                                            return (
                                                <BarTitleContainer>
                                                    <ChartHeading icon={<AccountBalanceWalletOutlinedIcon />} title="Unbilled Disbursements" />
                                                    <TotalDiv className="h2" onClick={() => this.handleTotalClick()}>
                                                        <AnimatedNumber value={totalAmount} format={(amount) => `${symbol}${formatNumber(amount)}`} />
                                                    </TotalDiv>
                                                    <div className="h3 rv-chart-section">Unbilled Disbursements by {GetGroupBy(this.props.groupBy)}</div>
                                                </BarTitleContainer>
                                            );
                                        }}
                                    </Query>
                                    <BarContainer className="rv-chart-bars">
                                        {/* tslint:disable-next-line: no-any */}
                                        <Query<any> query={CurrencySymbol}>
                                            {({
                                                // tslint:disable-next-line: no-shadowed-variable
                                                loading, error, data
                                            }) => {
                                                if (loading) {
                                                    return (
                                                        <LoadWrapper className="load-wrapper">
                                                            <RvLoader size="small" />
                                                        </LoadWrapper>
                                                    );
                                                }

                                                const currencySymbol = loading || error ? null : data.options.currencySymbol;

                                                const symbol = currencySymbol ? currencySymbol.symbol : null;

                                                return (
                                                    <>
                                                    <Bar
                                                        ref={this.chartRef}
                                                        // tslint:disable-next-line: no-any
                                                        onClick={(event: React.MouseEvent<any>) => {
                                                            const _data = getElementAtEvent(this.chartRef.current, event);
                                                            if (_data.length > 0 && this.props.getSelectedItem) {
                                                                const _index = getElementAtEvent(this.chartRef.current, event)[0].index;

                                                                const item = labels[_index];

                                                                const searchCriterias = new Array<SearchCriteria>();

                                                                searchCriterias.push(
                                                                    {
                                                                        property: this.props.groupBy,
                                                                        operator: 'eq',
                                                                        comparison: item,
                                                                    },
                                                                    {
                                                                        property: 'UnbilledDisbursements',
                                                                        operator: 'not',
                                                                        comparison: '0',
                                                                    },
                                                                );

                                                                this.props.getSelectedItem(
                                                                    searchCriterias,
                                                                    true,
                                                                    `Unbilled Disbursements by ${GetGroupBy(this.props.groupBy)}`,
                                                                    DrillDownType.matterSummaryDrilldown,
                                                                    'Matters',
                                                                );
                                                            }
                                                        }}
                                                        data={{
                                                            labels: getLabels(labels),
                                                            datasets: [
                                                                {
                                                                    label: 'Disbursements',
                                                                    ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                                                    data: amounts,
                                                                },
                                                            ],
                                                        }}
                                                        options={{
                                                            onHover: this.onHover,
                                                            indexAxis: 'y' as const,
                                                            animation: rvChartAnimation(),
                                                            elements: { bar: { borderWidth: 0 } },
                                                            responsive: true,
                                                            maintainAspectRatio: false,
                                                            scales: {
                                                                x: {
                                                                    grid: rvValueAxisStyle.grid,
                                                                    // beginAtZero: true,
                                                                    suggestedMax: getSuggestedMaxTick(amounts),
                                                                    beginAtZero: true,
                                                                    ticks: {
                                                                        ...rvValueAxisStyle.ticks,
                                                                        maxTicksLimit: getMaxTickLimit(amounts),
                                                                        autoSkip: false,
                                                                        callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                                                            return `${symbol}${formatNumber(tickValue)}`;
                                                                        },
                                                                    },
                                                                },
                                                                y: {
                                                                    grid: rvCategoryAxisStyle.grid,
                                                                    // min: 0,
                                                                    // max: 9 // minimum display
                                                                    ticks: {
                                                                        ...rvCategoryAxisStyle.ticks,
                                                                        autoSkip: false
                                                                    }
                                                                }
                                                            },
                                                            plugins: {
                                                                legend: {
                                                                    position: 'bottom' as const,
                                                                    display: false,
                                                                },
                                                                title: {
                                                                    display: false,
                                                                    text: `Estimate vs Billed Fees`,
                                                                },
                                                                zoom: {
                                                                    pan: {
                                                                        enabled: false,
                                                                        onPan: this.onPan,
                                                                    },
                                                                    zoom: {
                                                                        wheel: {
                                                                            enabled: false,
                                                                        },
                                                                    },
                                                                    // limits: {
                                                                    //   y: { min: 0, max: 9 },
                                                                    // },
                                                                },
                                                                tooltip: {
                                                                    ...rvTooltipStyle,
                                                                    callbacks: {
                                                                        // tslint:disable-next-line: no-any
                                                                        label: function (context: any) {
                                                                            const {
                                                                                dataIndex,
                                                                                //   label,
                                                                                //   formattedValue,
                                                                                //   dataset
                                                                            } = context;

                                                                            const currentValue = amounts[dataIndex];

                                                                            return `${symbol}${formatNumber(currentValue)}`;
                                                                        },
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                    />
                                                    {isChartDataEmpty(amounts) && (
                                                        <ChartEmptyState title="No unbilled disbursements" hint={"Nothing is unbilled right now."} />
                                                    )}
                                                    </>
                                                );
                                            }}
                                        </Query>
                                    </BarContainer>
                                    {labelCount > 10 && <ChartLimitNote>The top 10 {GetGroupByPlurals(this.props.groupBy)} are shown</ChartLimitNote>}
                                    <ChartNotes
                                        notes={['Disbursements only, fees are not included.', 'Information collated by Matter Responsibility']}
                                        actionHint="Drill down to matters"
                                    />
                                </BarWrapper>
                            );
                        }

                        return (
                            <LoadWrapper className="load-wrapper">
                                <RvLoader size="small" />
                            </LoadWrapper>
                        );
                    }}
                </Query>
            </React.Fragment>
        );
    }

    private handleTotalClick() {
        if (this.props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            searchCriterias.push({ property: 'UnbilledDisbursements', operator: 'not', comparison: '0' });

            this.props.getSelectedItem(searchCriterias, true, `Unbilled Disbursements by ${GetGroupBy(this.props.groupBy)}`, DrillDownType.matterSummaryDrilldown, 'Matters');
        }
    }

    private onCloseChart() {
        if (this.props.onCloseChart) {
            return this.props.onCloseChart(`isUnbilledDisbursementsBy${this.props.groupBy}ChartOpen`);
        }
    }

    // tslint:disable-next-line: no-any
    private handlePopoverOpen(event: any) {
        this.setAnchorEl(event.currentTarget);
    }

    private handlePopoverClose = () => {
        this.setAnchorEl(null);
    }

    // tslint:disable-next-line: no-any
    private setAnchorEl(anchorElValue: any) {
        this.setState({
            anchorEl: anchorElValue,
            open: Boolean(anchorElValue),
        });
    }

    private onPan = (context: { chart: Chart }) => {
        this.chartRef.current.options.scales.x.min = 0;
        this.chartRef.current.update();
    }

    private onHover(event: ChartEvent, elements: ActiveElement[], chart: Chart) {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    }
}

// tslint:disable-next-line: no-any
export default withStyles(styles as any, { withTheme: true })(UnbilledDisbursementsChart as any) as any;

const UnBilledDisbursementsData = gql`
    query UnBilledDisbursementsData($groupBy: MatterGroupBy, $entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            matter {
                unBilledDisbursements(groupBy: $groupBy, entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    total
                    groupedByCount
                    unBilledDisbursement {
                        amount
                        groupedBy
                    }
                }
            }
        }
    }
`;

const CurrencySymbol = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;
